/**
 * UsageMeter Component
 *
 * Displays a progress bar showing current usage vs subscription limits.
 * Automatically color-codes based on usage percentage:
 * - Green (< 80%): Normal usage
 * - Yellow (80-99%): Warning - approaching limit
 * - Red (>= 100%): At limit
 */

import React from 'react';
import styled from 'styled-components';

export interface UsageMeterProps {
  /** Label for the usage meter (e.g., "Members", "Staff Accounts") */
  label: string;
  /** Current usage count */
  current: number;
  /** Maximum limit from subscription plan */
  limit: number;
  /** Optional unit label (e.g., "GB", "members") */
  unit?: string;
  /** Height of the progress bar in pixels */
  height?: number;
  /** Whether to show the percentage */
  showPercentage?: boolean;
}

const Container = styled.div`
  margin-bottom: var(--spacing-md);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xs);
`;

const Label = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

const UsageText = styled.span`
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
`;

const ProgressContainer = styled.div<{ $height: number }>`
  width: 100%;
  height: ${props => props.$height}px;
  background-color: var(--background-secondary);
  border-radius: var(--radius-md);
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div<{ $percentage: number; $status: 'normal' | 'warning' | 'limit' }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  border-radius: var(--radius-md);
  transition: width 0.3s ease, background-color 0.3s ease;
  background-color: ${props => {
    switch (props.$status) {
      case 'limit': return 'var(--color-error, #dc3545)';
      case 'warning': return 'var(--color-warning, #ffc107)';
      default: return 'var(--color-success, #28a745)';
    }
  }};
`;

const PercentageLabel = styled.span<{ $status: 'normal' | 'warning' | 'limit' }>`
  font-size: var(--font-size-xs);
  margin-left: var(--spacing-xs);
  color: ${props => {
    switch (props.$status) {
      case 'limit': return 'var(--color-error, #dc3545)';
      case 'warning': return 'var(--color-warning, #b39600)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const getStatus = (percentage: number): 'normal' | 'warning' | 'limit' => {
  if (percentage >= 100) return 'limit';
  if (percentage >= 80) return 'warning';
  return 'normal';
};

export const UsageMeter: React.FC<UsageMeterProps> = ({
  label,
  current,
  limit,
  unit = '',
  height = 8,
  showPercentage = true,
}) => {
  const percentage = limit > 0 ? (current / limit) * 100 : 0;
  const status = getStatus(percentage);

  const formatNumber = (num: number): string => {
    if (Number.isInteger(num)) return num.toLocaleString();
    return num.toFixed(2);
  };

  return (
    <Container>
      <Header>
        <Label>{label}</Label>
        <div>
          <UsageText>
            {formatNumber(current)} / {formatNumber(limit)}{unit && ` ${unit}`}
          </UsageText>
          {showPercentage && (
            <PercentageLabel $status={status}>
              ({Math.round(percentage)}%)
            </PercentageLabel>
          )}
        </div>
      </Header>
      <ProgressContainer $height={height}>
        <ProgressFill
          $percentage={percentage}
          $status={status}
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-label={`${label}: ${current} of ${limit}${unit ? ` ${unit}` : ''}`}
        />
      </ProgressContainer>
    </Container>
  );
};

export default UsageMeter;
